import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { useSocket } from "../../context/SocketContext";
import { DropOverlay } from "../Messages/components/DropOverlay";
import { ImagePreviewModal } from "../Messages/components/ImagePreviewModal";
import { MessagesList } from "../Messages/components/MessageList";
import { Message } from "../Messages/components/Message/Message";
import { useMessageFileDrop } from "../Messages/hooks/useMessageFileDrop";
import { useMessagesScroll } from "../Messages/hooks/useMessageScroll";
import { RiveByKey } from "../riveAnimation";
import { GroupComposer } from "./components/GroupComposer";
import { MentionText } from "./components/MentionText";
import { SystemEventBubble } from "./components/SystemEventBubble";
import { useMessageGroupComposer } from "./hooks/useMessageGroupComposer";
import { useMessageGroupMessagesQuery } from "./hooks/useMessageGroupMessagesQuery";
import { useMessageGroupMutation } from "./hooks/useMessageGroupMutation";
import { useMessageGroupRead } from "./hooks/useMessageGroupRead";
import { useMessageGroupSocket } from "./hooks/useMessageGroupSocket";
import { useMessageGroupSummaryQuery } from "./hooks/useMessageGroupSummaryQuery";
import {
  buildMessageGroupSender,
  getGroupCompanyDisplayName,
  isEmployeeRole,
  isMentionedInMessage,
  maskGroupSenderForEmployee,
} from "./utils/messageGroupDisplay.utils";

interface MessageGroupViewProps {
  groupUid: string;
  companyId?: number | string | null;
  isFocused?: boolean;
  focusMessageUid?: string | null;
  focusMessageText?: string | null;
  focusMessageRequestKey?: string | null;
  onFocusMessageConsumed?: () => void;
}

export const MessageGroupView = ({
  groupUid,
  companyId: companyIdProp,
  isFocused = true,
  focusMessageUid,
  focusMessageText,
  focusMessageRequestKey,
  onFocusMessageConsumed,
}: MessageGroupViewProps) => {
  const { uid, workforces, company } = useSelector((state: any) => state.userState);
  const { socket } = useSocket();
  const currentRole = useAppSelector(selectRole);

  const { data: groupSummary, isLoading: isLoadingSummary } = useMessageGroupSummaryQuery({ groupUid, companyId: companyIdProp });
  const companyId = companyIdProp ?? groupSummary?.companyId;

  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string | null; name: string | null }>({
    open: false,
    url: null,
    name: null,
  });

  const sender = useMemo(
    () => buildMessageGroupSender(companyId, { uid, workforces, currentRole }),
    [companyId, uid, workforces, currentRole],
  );

  const { messagesQuery } = useMessageGroupMessagesQuery({ groupUid, companyId });
  const hasMessages = useMemo(
    () => messagesQuery.data?.pages?.some((page: any) => (page?.items?.length ?? 0) > 0) ?? false,
    [messagesQuery.data],
  );
  const { messagesMutation } = useMessageGroupMutation(groupUid, companyId);
  const { markRead } = useMessageGroupRead(groupUid, companyId);
  const { messagesContainerRef, scrollToBottom, handleScroll } = useMessagesScroll(messagesQuery);

  useEffect(() => {
    if (groupUid && isFocused) markRead();
  }, [groupUid, markRead, isFocused]);

  const handleIncomingMessage = useCallback(() => {
    scrollToBottom();
    if (isFocused) markRead();
  }, [scrollToBottom, markRead, isFocused]);

  const [othersLastReadAt, setOthersLastReadAt] = useState<string | null>(null);

  const handleRead = useCallback(
    (reader: { type: string; uid?: string } | undefined, lastReadAt: string) => {
      if (reader?.type === sender?.type && reader?.uid === sender?.uid) return;
      setOthersLastReadAt((previous) => (!previous || new Date(lastReadAt) > new Date(previous) ? lastReadAt : previous));
    },
    [sender?.type, sender?.uid],
  );

  const resolveIsRead = useCallback(
    (message: any) => (othersLastReadAt ? new Date(message.createdAt) <= new Date(othersLastReadAt) : false),
    [othersLastReadAt],
  );

  useMessageGroupSocket(socket, groupUid, companyId, handleIncomingMessage, undefined, handleRead);

  const companyDisplayName = getGroupCompanyDisplayName(company);

  const messagesWithMemberRoles = useMemo(() => {
    if (!messagesQuery.data || !groupSummary) return messagesQuery;

    const members = groupSummary.members || [];
    return {
      ...messagesQuery,
      data: {
        ...messagesQuery.data,
        pages: messagesQuery.data.pages.map((page: any) => ({
          ...page,
          items: (page.items || []).map((message: any) => {
            const member = members.find((item) => item.uid === message.sender?.uid);
            const enriched = member
              ? {
                  ...message,
                  sender: {
                    ...message.sender,
                    avatar: message.sender?.avatar || member.avatar,
                    roleType:
                      member.roleName?.toLowerCase() === "business" ? "Owner" : message.sender?.roleType || member.roleName || member.role,
                  },
                }
              : message;

            return maskGroupSenderForEmployee(enriched, sender, companyDisplayName, currentRole);
          }),
        })),
      },
    };
  }, [messagesQuery, groupSummary, sender, currentRole, companyDisplayName]);

  const isUndeliverableGroupText = useMemo(
    () => groupSummary?.channel === "sms" && (groupSummary?.members ?? []).length < 3,
    [groupSummary],
  );

  const handleMessageSent = useCallback(() => scrollToBottom(), [scrollToBottom]);

  const composer = useMessageGroupComposer({
    messagesMutation,
    groupUid,
    companyId,
    members: groupSummary?.members ?? [],
    onMessageSent: handleMessageSent,
  });

  const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useMessageFileDrop((files: File[]) => {
    composer.handleDragAndDropImages(files);
    composer.handleDragAndDropFiles(files);
  });

  const handleOpenImagePreview = (url: string, name: string) => setImagePreview({ open: true, url, name });
  const handleCloseImagePreview = () => setImagePreview({ open: false, url: null, name: null });

  const renderMessage = useCallback(
    (message: any) => {
      if (message?.kind === "system") {
        const actorName = isEmployeeRole(currentRole) && message.sender?.type !== "workforce" ? companyDisplayName : message.sender?.name || "Someone";
        return <SystemEventBubble message={message} actorName={actorName} />;
      }

      return (
        <Message
          message={message}
          sender={sender}
          onOpenImagePreview={handleOpenImagePreview}
          resolveIsRead={resolveIsRead}
          textComponent={MentionText}
          bubbleAccent={isMentionedInMessage(message, sender) ? "#E87722" : undefined}
        />
      );
    },
    [currentRole, companyDisplayName, sender, resolveIsRead],
  );

  if (isLoadingSummary || messagesQuery?.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (messagesQuery?.isError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "error.main", fontWeight: 600 }}>
        {(messagesQuery?.error as Error)?.message || "Error loading messages"}
      </Box>
    );
  }

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      {isDragging && <DropOverlay />}

      {hasMessages ? (
        <MessagesList
          handleScroll={handleScroll}
          messagesContainerRef={messagesContainerRef}
          messagesQuery={messagesWithMemberRoles}
          sender={sender}
          onOpenImagePreview={handleOpenImagePreview}
          renderMessage={renderMessage}
          pendingMessages={composer.pendingMessages}
          onRetryMessage={composer.retryPendingMessage}
          resolveIsRead={resolveIsRead}
          focusMessageUid={focusMessageUid}
          focusMessageText={focusMessageText}
          focusMessageRequestKey={focusMessageRequestKey}
          onFocusMessageConsumed={onFocusMessageConsumed}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            <RiveByKey riveKey="sectorworkflow" boxSize={170} />
          </Box>
          <Typography sx={{ mt: -3.5, color: "#f57c00", fontSize: "0.9rem", fontWeight: 800, textAlign: "center" }}>No messages yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.8rem", textAlign: "center" }}>
            Say hello to {groupSummary?.name ?? "the group"}.
          </Typography>
        </Box>
      )}

      {isUndeliverableGroupText && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.75, pt: 0.75 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "warning.main" }} />
          <Typography sx={{ fontSize: 10.5, color: "warning.main" }}>
            Only {(groupSummary?.members ?? []).length} participants left · a group text needs at least 3 to deliver
          </Typography>
        </Box>
      )}

      <GroupComposer composer={composer} />

      <ImagePreviewModal open={imagePreview.open} onClose={handleCloseImagePreview} imageUrl={imagePreview.url} imageName={imagePreview.name} />
    </Box>
  );
};
